import React, { useState, useMemo, useContext } from "react";

const SignUpContext = React.createContext<SignUpContextType | undefined>(
  undefined,
);

const SignUpBreadcrumbLabels = [
  {
    key: "sign-up",
  },
  {
    key: "identification",
  },
  {
    key: "verification",
  },
  {
    key: "success",
  },
];

const SignInTermsBreadcrumbLabels = [
  {
    key: "sign-up",
  },
  {
    key: "confirmation",
  },
  {
    key: "success",
  },
];

type SignUpContextProviderProps = {
  readonly children: React.ReactNode;
};

type SignUpDetails = {
  firstName?: string;
  lastName?: string;
  email?: string;
  redirectUrl?: string;
};

type SignUpContextType = {
  signUp: {
    signUpDetails: SignUpDetails;
    updateSignUpDetails: (values: SignUpDetails) => void;
  };
};

function SignUpContextProvider({ children }: SignUpContextProviderProps) {
  const [signUpDetails, setSignUpDetails] = useState(() => {
    const storedData = sessionStorage.getItem("signUpUser");
    return storedData
      ? JSON.parse(storedData)
      : {
          firstName: undefined,
          lastName: undefined,
          email: undefined,
          redirectUrl: undefined,
        };
  });

  const updateSignUpDetails = (values: SignUpDetails) => {
    const user = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      redirectUrl: values.redirectUrl,
    };
    sessionStorage.setItem("signUpUser", JSON.stringify(user));
    setSignUpDetails(user);
  };

  const context: SignUpContextType = useMemo(
    () => ({
      signUp: { signUpDetails, updateSignUpDetails },
    }),
    [signUpDetails],
  );

  return (
    <SignUpContext.Provider value={context}>{children}</SignUpContext.Provider>
  );
}

const useSignUp = () => {
  const context = useContext(SignUpContext);
  if (context === undefined) {
    throw new Error("useSignUp must be used within a SignUpContextProvider");
  }
  return context.signUp;
};

export {
  SignUpContextProvider,
  SignInTermsBreadcrumbLabels,
  SignUpBreadcrumbLabels,
  useSignUp,
};
